import { useCallback, useMemo } from 'react';
import { useLocalStorage } from '@ai-tutor/ui';

import { track } from '@/config/observability';

/**
 * The lesson text sections this student has marked as read, on this device -
 * the contents list and the end of each section show the mark. Set by the
 * student, never by opening a section (opening one is not reading it). A
 * convenience only: lost with the browser's storage. The list itself is never
 * sent anywhere; marking a section read is counted as a `section_read`
 * analytics event (index and counts only).
 */
export function useReadSections(
  lessonId: string | undefined,
  sectionsTotal = 0
): [Set<number>, (index: number, read: boolean) => void] {
  const [stored, setStored] = useLocalStorage<number[]>(
    `xonera:text-read:${lessonId ?? 'none'}`,
    []
  );
  const list = useMemo(() => (Array.isArray(stored) ? stored : []), [stored]);
  const setRead = useCallback(
    (index: number, read: boolean) => {
      const without = list.filter(i => i !== index);
      setStored(read ? [...without, index] : without);
      if (read && lessonId && !list.includes(index)) {
        track('section_read', {
          lesson_id: lessonId,
          section_index: index,
          sections_total: sectionsTotal,
          sections_read: without.length + 1,
        });
        // "Completed" = every text section marked read, the one finish line
        // the learner draws themselves.
        if (sectionsTotal > 0 && without.length + 1 === sectionsTotal) {
          track('lesson_completed', {
            lesson_id: lessonId,
            sections_total: sectionsTotal,
          });
        }
      }
    },
    [list, setStored, lessonId, sectionsTotal]
  );
  const read = useMemo(() => new Set(list), [list]);
  return [read, setRead];
}
